import type {ReactNode} from "react";
import React, { useCallback, useMemo} from "react"
import DatePicker from 'react-datepicker'
import Moment from 'moment'
import icons from '../../../markons/markons'
import type {ClassValue} from '../../helpers/react-helpers'
import AioText from "./AioText"
import css from './aio.less'
import {useTestId} from '../../contexts/testid'
import DisplayContents from '../DisplayContents'
import BodyEnd from '../BodyEnd'

interface Props /*extends Pick<React.ComponentProps<DatePicker>, 'onChange' | 'disabled' | 'onFocus' | 'onBlur'>*/
{
    id?: string
    width?: number | string
    minWidth?: number | string
    maxWidth?: number | string
    label: string
    className?: ClassValue
    value: string
    onChange: AioDatepickerOnChangeHandler
    placeholder?: string
    minDate?: Date
    maxDate?: Date
    inline?: boolean
    testId?: string
    required?: boolean
    disabled?: boolean
}

const INTERNAL_DATE_FORMAT = 'YYYY-MM-DD' // https://momentjs.com/docs/#/displaying/format/
const DISPLAY_DATE_FORMAT = 'dd LLL yy' // https://date-fns.org/v2.11.1/docs/format


export type AioDatepickerOnChangeEvent = string
export type AioDatepickerOnChangeHandler = (date: AioDatepickerOnChangeEvent) => void

export default function AioDatepicker({id, value, width, minWidth, maxWidth, label, testId,disabled, className, onChange, placeholder, minDate, maxDate, inline, required}: Props) {
    const dateValue = useMemo(() => value ? Moment(value, INTERNAL_DATE_FORMAT).toDate() : null, [value])
    const prefixedTestId = useTestId(testId)

    const changeHandler = useCallback((date: Date|null) => {
        const dateStr = Moment(date).format(INTERNAL_DATE_FORMAT)
        onChange && onChange(dateStr)
    }, [onChange])

    // eslint-disable-next-line react/display-name
    const CustomInput = React.forwardRef<HTMLInputElement,AnyObject>((props, ref) => {
        return (
            <AioText
                required={required}
                id={id}
                onClick={props.onClick}
                value={props.value}
                readOnly={true}
                ref={ref}
                icon={icons.faCalendarDay}
                label={label}
                placeholder={placeholder}
                disabled={disabled}
                width={width}
                minWidth={minWidth}
                maxWidth={maxWidth}
                inline={inline}
                testId={testId && `${testId}-input`}
            />
        )
    })

    const PopperContainer = function PopperContainer({children}: ({ children?:ReactNode })) {
        if(!children) return null
        return (
            <BodyEnd>
                <DisplayContents data-testid={prefixedTestId && `${prefixedTestId}-popup`}>{children}</DisplayContents>
            </BodyEnd>
        )
    }

    return <DatePicker selected={dateValue} dateFormat={DISPLAY_DATE_FORMAT} onChange={changeHandler}
                       minDate={minDate} maxDate={maxDate} disabled={disabled} customInput={<CustomInput/>} wrapperClassName={inline ? css.inline : undefined}
                       popperContainer={PopperContainer}/>

    // keyboard fix: https://github.com/Hacker0x01/react-datepicker/issues/1640#issuecomment-505486891
    // m0ar keyboard: https://github.com/Hacker0x01/react-datepicker/issues/1480
}

